import { GridRowModel, GridRowOrderChangeParams, GridSortModel } from '@mui/x-data-grid-premium';
import { parseISO } from 'date-fns';
import { fromPairs, get } from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Note, Production, Reference } from 'sr-types/lib/search/v1/graphql';
import { onSaveEntityAfterAttributeEdit } from '../attributes/attributesUtils';
import Attachments from '../common/attachments/Attachments';
import { SECURITY_ACTIONS, useEntitySecurityEvaluator, type EntityName } from '../common/auth/Security';
import { UserContext } from '../common/auth/UserContext';
import type { MainSidePanelProps } from '../common/components/layout/LayoutUtils';
import WithSidePanel from '../common/components/layout/WithSidePanel';
import { FILTER_IDENTIFIERS, SIDE_PANEL_IDS } from '../common/constants/constants';
import { ENTITY_CONSTANTS } from '../common/constants/entityConstants';
import { useInitializeListViewDeepLinkUIState } from '../common/deepLink';
import type { DeepLinkUIStateOnBeforeInitializationHandler } from '../common/deepLink/types';
import { useOnMobile } from '../common/hooks/breakpoints';
import { useBrowserOrAccountLocale } from '../common/hooks/useBrowserOrAccountLocale';
import I18n, { useI18n } from '../common/i18n/I18n';
import { Icons } from '../common/icons/Icons';
import { V } from '../common/Layout';
import DataGrid from '../common/list/DataGrid';
import type { DataGridCustomRowAction } from '../common/list/DataGrid/types';
import { useSaveRanks } from '../common/list/rank';
import { reorderRowsWithRanks } from '../common/list/rankUtils';
import StatusReportsPanel from '../common/list/StatusReportsPanel';
import { PROFILES, RESERVATION_PROFESSIONAL, SUPPLIER } from '../common/nav/apps';
import Notes from '../common/notes/Notes';
import { toYearFromISOString } from '../common/utils/dateTime';
import useHistory from '../common/utils/useHistory';
import CreateProductionForm from './form/CreateProductionForm';
import { useSaveProduction } from './helpers/productionApi';
import { onTagChanged } from './helpers/productionUtils';
import useProductionGridColumns from './helpers/useProductionGridColumns';
import { useAssociateStatusNoteToProduction } from './hooks/associateProductionStatusNote';
import ProductionAttributesSidePanel from './ProductionAttributesSidePanel';
import { CONTEXT_TERM } from './productionConstants';
import { ProductionModes } from './ProductionList';
import { getItemsWithNewStatusNote } from './productionUtils/productionListViewUtils';

const EMPTY_SORT_MODEL: GridSortModel = [];

const ProductionRankOrderList = ({
    entity,
    feature,
    data: raw,
    uri,
    history,
    onChangeData,
    controlledSidePanelId,
    onControlledSidePanelClose,
    ...props
}) => {
    const t = useI18n();
    const { application } = useContext(UserContext);
    const { changeRoute, searchParams } = useHistory();
    const { associateProductionStatusNote, statusNoteSaving } = useAssociateStatusNoteToProduction();
    const { onSaveProduction } = useSaveProduction();
    const isMobile = useOnMobile();
    const locale = useBrowserOrAccountLocale();

    const productionMode = searchParams.get('productionMode');
    const isProductionMode = useMemo(() => {
        return productionMode === ProductionModes.production;
    }, [productionMode]);

    const evaluateEntitySecurity = useEntitySecurityEvaluator();

    const columns = useProductionGridColumns(isProductionMode, [
        t('grid.header.production.years'),
        t('grid.header.season'),
        t('grid.header.createdBy'),
        t('grid.header.createdOn')
    ]);

    const [editingId, setEditingId] = useState<string>();
    const [entityReference, setEntityReference] = useState<Reference>();
    const [rowEntityName, setRowEntityName] = useState<string>();
    const [sidePanelOpened, setSidePanelOpened] = useState<string>();
    const [currentStatusNote, setCurrentStatusNote] = useState<Note>();
    const [focusField, setFocusField] = useState<string>();
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const { onSaveRanks } = useSaveRanks('common.rank.update.success', 'common.rank.update.failure');

    const handleSidePanelOpening = (row: Production) => {
        onControlledSidePanelClose?.();
        setEntityReference(row.reference);
        setRowEntityName(`${row[FILTER_IDENTIFIERS.name]}`);
    };

    const columnVisibility = fromPairs(
        columns.filter((column) => get(column, 'hide') === true).map((key) => [key.field, false])
    );

    useEffect(() => {
        const mappedRows = raw?.results?.hits?.items.map((entity, index) => {
            const otherTitlesValue = entity.otherTitles
                ? entity.otherTitles.find((title) => title.key === 'AKA')?.value
                : '';
            const entityReleaseDate = entity.releaseDate !== '' ? toYearFromISOString(entity.releaseDate, locale) : '';
            const seasonNumber = entity.showAttributes
                ? entity.showAttributes.find((attribute) => attribute.key === 'seasonNo')?.value || ''
                : '';
            return {
                key: index,
                ...entity,
                publictitle: entity.name,
                productiontype: entity.productionType,
                productionStatus: entity.productionStatus,
                otherTitles: otherTitlesValue,
                releaseDate: parseISO(entity.releaseDate),
                releaseYear: entityReleaseDate,
                ProductionYears: entity.productionYears,
                seasonNumber: seasonNumber,
                productionGroup: entity.productionGroup?.label || entity.productionGroup?.productionReference?.id,
                createdOn: parseISO(entity.createdOn),
                updatedOn: parseISO(entity.updatedOn),
                security: entity.security,
                statusNote: entity.currentStatusNote,
                rank: entity.rank || null,
                __reorder__: entity.name,
                _raw: entity
            };
        });
        setRows(mappedRows || []);
    }, [locale, raw]);

    const entitySecurityProviderProps: MainSidePanelProps['entitySecurityProviderProps'] = useMemo(() => {
        const entityName = ENTITY_CONSTANTS.production;
        const entitySecurity = rows.find((row) => row.reference.id === entityReference?.id)?.security ?? {};
        return {
            entityName,
            entitySecurity
        };
    }, [entityReference?.id, rows]);

    const handleAttachmentsRowAction = useCallback(
        (row: Production) => {
            handleSidePanelOpening(row);
            setSidePanelOpened(SIDE_PANEL_IDS.attachments);
        },
        [handleSidePanelOpening]
    );

    const handleNotesRowAction = useCallback(
        (row: Production & { statusNote?: Note }) => {
            handleSidePanelOpening(row);
            setSidePanelOpened(SIDE_PANEL_IDS.notes);
            setCurrentStatusNote(row.statusNote);
        },
        [handleSidePanelOpening]
    );

    // Note: customRowActions is intentionally reduced in rank-order list (only attachments and notes).
    // Attributes and tags editing are not part of rank-order workflow; use ProductionDataGrid for full CRUD.
    const customRowActions = useMemo((): DataGridCustomRowAction[] => {
        if (isProductionMode) {
            return [];
        }

        return [
            {
                securityAction: SECURITY_ACTIONS.COMMENT,
                labelToken: 'attachments.title',
                icon: <Icons.AttachFileOutlined fontSize="small" />,
                onClick: handleAttachmentsRowAction
            },
            {
                securityAction: SECURITY_ACTIONS.COMMENT,
                labelToken: 'notes.title',
                icon: <Icons.Notes fontSize="small" />,
                onClick: handleNotesRowAction
            }
        ];
    }, [isProductionMode, handleAttachmentsRowAction, handleNotesRowAction]);

    const handleOnAssociateStatusNote = (note: Note) => {
        const localCurrentStatusNote = currentStatusNote;
        onStatusNoteChanged(note, entityReference.id);
        associateProductionStatusNote({
            productionId: entityReference.id,
            note: note.reference
        })
            .then((reference: Reference) => {
                if (!reference?.id) {
                    setCurrentStatusNote(localCurrentStatusNote);
                }
            })
            .catch(() => setCurrentStatusNote(localCurrentStatusNote));
    };

    const onStatusNoteChanged = (note: Note | null, productionId: string) => {
        setCurrentStatusNote(note);
        const newItems = raw?.results?.hits?.items.map((item: Production) =>
            getItemsWithNewStatusNote(item, productionId, note)
        );
        onChangeData(newItems);
    };

    const resetEditingState = () => {
        setEditingId(undefined);
        setFocusField(undefined);
    };

    const onEdit = useCallback((id: string) => setEditingId(id), [setEditingId]);
    const rankedGroupId = '0'; // Prefix for ranked bucket of productions

    const handleRowOrderChange = async (params: GridRowOrderChangeParams) => {
        setLoading(true);
        const newRows = await updateRowPosition(params.oldIndex, params.targetIndex, rows, rankedGroupId);
        setRows(newRows);
        setLoading(false);
    };

    function updateRowPosition(
        initialIndex: number,
        targetIndex: number,
        rows: Array<GridRowModel>,
        groupId: string
    ): Promise<Array<GridRowModel>> {
        return new Promise((resolve) => {
            const originalRows = [...rows];
            const updatedRows = reorderRowsWithRanks(initialIndex, targetIndex, rows, groupId);

            // Tracking affected rows for persisting updates to backend
            const affectedRows = updatedRows.filter((row) => {
                const originalRow = rows.find((original) => original.id === row.id);
                return originalRow && originalRow.rank !== row.rank;
            });

            const rankInputs = affectedRows.map((row) => ({
                entity: row.reference,
                rank: row.rank
            }));

            resolve(updatedRows);

            // Persist updated ranks to backend
            if (rankInputs.length > 0) {
                onSaveRanks(rankInputs)
                    .then((resp) => {
                        let shouldRollback = false;
                        if (resp === null) {
                            console.error('Error saving ranks: onSaveRanks returned null (errors in response data)');
                            shouldRollback = true;
                        } else if (!Array.isArray(resp)) {
                            console.error('Error saving ranks: onSaveRanks returned invalid response format');
                            shouldRollback = true;
                        }
                        if (shouldRollback) {
                            setRows(originalRows);
                        }
                    })
                    .catch((err) => {
                        setRows(originalRows);
                        console.error('Error saving ranks: ', err);
                    });
            }
        });
    }

    const onBeforeInitializeDeepLinkUIState: DeepLinkUIStateOnBeforeInitializationHandler = useCallback(
        ({ entityId, sidePanelId }) => {
            if (!entityId || !sidePanelId) {
                return;
            }

            const matchingRow = rows.find((row) => row.reference?.id === entityId);

            if (matchingRow?.reference) {
                handleSidePanelOpening(matchingRow);
                setSidePanelOpened(sidePanelId);
                if (sidePanelId === SIDE_PANEL_IDS.notes) {
                    setCurrentStatusNote((matchingRow.statusNote as Note | undefined) ?? undefined);
                }
            }
        },
        [handleSidePanelOpening, rows]
    );

    const getDeepLinkEntityDataRecordId = useCallback((row: Production) => row.reference?.id, []);

    useInitializeListViewDeepLinkUIState({
        entity: ENTITY_CONSTANTS.production,
        entityDataList: raw?.results?.hits?.items ? rows : undefined,
        selectedEntityDataRecordId: entityReference?.id,
        getEntityDataRecordId: getDeepLinkEntityDataRecordId,
        onBeforeInitialize: onBeforeInitializeDeepLinkUIState
    });

    const securedPanels = useMemo(
        () => [
            {
                id: SIDE_PANEL_IDS.attributes,
                icon: Icons.ResourceFields,
                label: <I18n token="attributes.title" />,
                securityAction: SECURITY_ACTIONS.EDIT,
                entityName: entitySecurityProviderProps.entityName,
                component: (onClose) => {
                    if (!entityReference?.id) {
                        return null;
                    }

                    return (
                        <ProductionAttributesSidePanel
                            entityId={entityReference.id}
                            onClose={() => {
                                setEntityReference(undefined);
                                setRowEntityName(undefined);
                                setSidePanelOpened(undefined);
                                onClose();
                            }}
                            entityName={rowEntityName ?? ''}
                            entity={ENTITY_CONSTANTS.production}
                            onSaveEntity={(updatedValues, isDeletingAttribute) =>
                                onSaveEntityAfterAttributeEdit(
                                    updatedValues,
                                    isDeletingAttribute,
                                    onSaveProduction,
                                    entityReference.id,
                                    locale
                                )
                            }
                        />
                    );
                }
            },
            {
                id: 'notes',
                icon: Icons.Notes,
                label: <I18n token="notes.title" />,
                securityAction: SECURITY_ACTIONS.COMMENT,
                entityName: entitySecurityProviderProps.entityName,
                disabled: !entityReference,
                component: (onClose) => (
                    <Notes
                        id={'notes-production'}
                        title={<I18n token="notes.panel.title" values={{ entityName: rowEntityName }} />}
                        entity={ENTITY_CONSTANTS.production}
                        entityReference={entityReference}
                        onClose={() => {
                            setEntityReference(undefined);
                            onClose();
                        }}
                        onAssociateStatusNote={{
                            saveFunction: (note: Note) => handleOnAssociateStatusNote(note),
                            loadingState: statusNoteSaving,
                            statusNote: currentStatusNote,
                            onStatusNoteChanged
                        }}
                    />
                )
            },
            {
                id: 'attachments',
                icon: Icons.Attachments,
                label: <I18n token="attachments.title" />,
                securityAction: SECURITY_ACTIONS.COMMENT,
                entityName: entitySecurityProviderProps.entityName,
                tooltip: entityReference ? <I18n token="atttachments.close" /> : <I18n token="attachment.from.grid" />,
                disabled: !entityReference,
                component: (onClose) => (
                    <Attachments
                        id={'attachments-production'}
                        entityReference={entityReference}
                        title={<I18n token="attachments.panel.title" values={{ entityName: rowEntityName }} />}
                        onClose={() => {
                            setEntityReference(undefined);
                            onClose();
                        }}
                        entity={entitySecurityProviderProps.entityName}
                    />
                )
            },
            {
                id: SIDE_PANEL_IDS.statusReports,
                icon: Icons.Visibility,
                hidden: true,
                securityAction: SECURITY_ACTIONS.READ,
                entityName: entitySecurityProviderProps.entityName,
                component: (onClose) => (
                    <StatusReportsPanel
                        featureName={feature}
                        contextTerm={CONTEXT_TERM.workspaceStatusReport}
                        onClose={() => {
                            onControlledSidePanelClose?.();
                            setEntityReference(undefined);
                            setRowEntityName(undefined);
                            setSidePanelOpened(undefined);
                            onClose();
                        }}
                    />
                )
            }
        ],
        [
            currentStatusNote,
            entityReference,
            entitySecurityProviderProps.entityName,
            feature,
            handleOnAssociateStatusNote,
            locale,
            onControlledSidePanelClose,
            onSaveProduction,
            onStatusNoteChanged,
            rowEntityName,
            statusNoteSaving
        ]
    );

    return (
        <WithSidePanel
            visibleSidePanelId={controlledSidePanelId || (entityReference ? sidePanelOpened : undefined)}
            entitySecurityProviderProps={entitySecurityProviderProps}
            securedPanels={securedPanels}
            leftToolbar={undefined}
            hideHeader={true}
            enableDeepLink
        >
            <V sx={{ height: '100%', minWidth: '1px' }}>
                <DataGrid
                    id={'Production'}
                    {...props}
                    asIsColumns={['managed', 'avatar']}
                    clickableColumn={'publictitle'}
                    feature={feature}
                    entityName={entity as EntityName}
                    rowCount={raw?.results?.summary?.total}
                    rows={raw?.results ? rows : []}
                    columns={columns}
                    forceDefaultColumnVisibility
                    defaultColumnVisibility={columnVisibility}
                    // Force server-side sorting with no client sort model so rank view
                    // is not affected by ListView's client-side sort state
                    sortingMode={'server'}
                    sortModel={EMPTY_SORT_MODEL}
                    onDetails={(id, row) => {
                        if ([SUPPLIER.home, RESERVATION_PROFESSIONAL.home].includes(application.home)) {
                            changeRoute(`${application.home}/production/${id}/details`);
                        } else if (application.home === PROFILES.home) {
                            const {
                                allowed: [canRead, canEdit]
                            } = evaluateEntitySecurity([SECURITY_ACTIONS.READ, SECURITY_ACTIONS.EDIT], {
                                entityName: ENTITY_CONSTANTS.production,
                                entitySecurity: row.security
                            });
                            const isReadOnly = canRead && !canEdit;
                            changeRoute(`${application.home}/production/${id}/details`, undefined, false, {
                                isReadOnly: isReadOnly
                            });
                        } else if (productionMode === ProductionModes.production) {
                            const routePrefix = row?.managedProduction ? 'workspace' : 'production';
                            const productionMode = row?.managedProduction
                                ? ProductionModes.workspace
                                : ProductionModes.production;

                            changeRoute(`${application.home}/${routePrefix}/${id}/details`, {
                                productionMode
                            });
                        } else {
                            changeRoute(`${application.home}/workspace/${id}/details`, {
                                productionMode: ProductionModes.workspace,
                                'prod-details_tab': '0'
                            });
                        }
                    }}
                    disableColumnSorting
                    customRowActions={customRowActions}
                    rowReordering
                    onRowOrderChange={handleRowOrderChange}
                    onEdit={onEdit}
                />
            </V>
            {!!editingId && (
                <CreateProductionForm
                    title={<I18n token={'production.form.edit.title'} />}
                    isOpen={!!editingId}
                    productionId={editingId}
                    focusField={focusField}
                    onCancel={resetEditingState}
                    onSave={(productionId, production) => {
                        onTagChanged(raw?.results?.hits?.items || [], production, onChangeData);
                        resetEditingState();
                    }}
                />
            )}
        </WithSidePanel>
    );
};

export default ProductionRankOrderList;
